import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
  BG_PAGE,
} from '../components/legacy-ball/table-management/designTokens';

// ── Interfaces ──────────────────────────────────────────────

interface PageEntry {
  id: string;
  slug: string;
  displayName: string;
  alias: string | null;
}

// ── Styles ──────────────────────────────────────────────────

const iconBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 6px',
  fontSize: '0.9rem',
  borderRadius: '4px',
  lineHeight: 1,
  transition: 'background-color 0.15s',
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '1.5rem 2rem',
    maxWidth: '1100px',
    margin: '0 auto',
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
  },
  header: {
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    margin: 0,
  },
  searchBar: {
    marginBottom: '1rem',
  },
  searchInput: {
    width: '320px',
    padding: '8px 12px',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '13px',
    boxSizing: 'border-box' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px 16px',
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: TEXT_MUTED,
    borderBottom: `2px solid ${PRIMARY}`,
    fontWeight: 600,
  },
  td: {
    padding: '10px 16px',
    fontSize: '14px',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: '#333',
    verticalAlign: 'middle' as const,
  },
  aliasInput: {
    padding: '6px 10px',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '13px',
    width: '180px',
    boxSizing: 'border-box' as const,
  },
  successIndicator: {
    display: 'inline-block',
    fontSize: '0.8rem',
    color: '#2e7d32',
    marginLeft: '6px',
    fontWeight: 600,
  },
  errorIndicator: {
    display: 'inline-block',
    fontSize: '0.8rem',
    color: '#d32f2f',
    marginLeft: '6px',
    fontWeight: 500,
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center' as const,
    color: TEXT_MUTED,
    fontSize: '1.1rem',
  },
};

// ── Component ───────────────────────────────────────────────

export function ScreenAliasManagePage() {
  const { user } = useAuth();
  const [pages, setPages] = useState<PageEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [rowStatus, setRowStatus] = useState<Record<string, { type: 'success' | 'error'; message: string }>>({});

  // ── Access Check ────────────────────────────────────────
  if (user?.role !== 'Admin') {
    return <div style={styles.accessDenied}>Access Denied</div>;
  }

  // ── Fetch Pages ─────────────────────────────────────────
  const fetchPages = useCallback(async () => {
    try {
      const data = await apiClient<PageEntry[]>('/page-registry');
      setPages(data);
    } catch {
      // silent fail — table will be empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  // ── Computed ────────────────────────────────────────────
  const aliasCount = pages.filter((p) => p.alias).length;
  const totalCount = pages.length;

  const filteredPages = pages.filter((p) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      p.slug.toLowerCase().includes(term) ||
      p.displayName.toLowerCase().includes(term) ||
      (p.alias && p.alias.toLowerCase().includes(term))
    );
  });

  // ── Inline Edit Handlers ────────────────────────────────
  const startEdit = (page: PageEntry) => {
    setEditingSlug(page.slug);
    setEditValue(page.alias || '');
    // Clear any previous status for this row
    setRowStatus((prev) => {
      const next = { ...prev };
      delete next[page.slug];
      return next;
    });
  };

  const cancelEdit = () => {
    setEditingSlug(null);
    setEditValue('');
  };

  const saveAlias = async (slug: string) => {
    const trimmed = editValue.trim();
    const aliasToSend = trimmed === '' ? null : trimmed;

    try {
      await apiClient(`/page-registry/${slug}/alias`, {
        method: 'PATCH',
        body: { alias: aliasToSend },
      });

      // Update local state
      setPages((prev) =>
        prev.map((p) => (p.slug === slug ? { ...p, alias: aliasToSend } : p)),
      );
      setEditingSlug(null);
      setEditValue('');

      // Show success indicator briefly
      setRowStatus((prev) => ({ ...prev, [slug]: { type: 'success', message: '✓ Saved' } }));
      setTimeout(() => {
        setRowStatus((prev) => {
          const next = { ...prev };
          delete next[slug];
          return next;
        });
      }, 3000);
    } catch (err: any) {
      if (err?.statusCode === 409) {
        setRowStatus((prev) => ({ ...prev, [slug]: { type: 'error', message: 'Alias already in use' } }));
      } else {
        setRowStatus((prev) => ({ ...prev, [slug]: { type: 'error', message: 'Failed to save' } }));
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, slug: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveAlias(slug);
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  // ── Render ──────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.page}>
        <p style={{ color: TEXT_MUTED }}>Loading screens...</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Screen Alias Management</h1>
        <p style={styles.subtitle}>
          {aliasCount} of {totalCount} screens have aliases
        </p>
      </div>

      <div style={styles.searchBar}>
        <input
          type="text"
          placeholder="Search by slug, display name, or alias..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
          aria-label="Search screens"
        />
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Screen ID</th>
            <th style={styles.th}>Display Name</th>
            <th style={styles.th}>Route Path</th>
            <th style={styles.th}>Current Alias</th>
            <th style={styles.th}>Edit</th>
          </tr>
        </thead>
        <tbody>
          {filteredPages.map((page) => (
            <tr key={page.slug}>
              <td style={styles.td}>{page.slug}</td>
              <td style={styles.td}>{page.displayName}</td>
              <td style={styles.td}>/{page.slug}</td>
              <td style={styles.td}>
                {editingSlug === page.slug ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value.slice(0, 50))}
                      onKeyDown={(e) => handleKeyDown(e, page.slug)}
                      style={styles.aliasInput}
                      maxLength={50}
                      autoFocus
                      aria-label={`Edit alias for ${page.slug}`}
                    />
                    <button
                      style={iconBtn}
                      onClick={() => saveAlias(page.slug)}
                      title="Save"
                      aria-label={`Save alias for ${page.slug}`}
                    >
                      💾
                    </button>
                    <button
                      style={iconBtn}
                      onClick={cancelEdit}
                      title="Cancel"
                      aria-label={`Cancel editing alias for ${page.slug}`}
                    >
                      ✕
                    </button>
                  </span>
                ) : (
                  <span>
                    {page.alias || <span style={{ color: TEXT_MUTED, fontStyle: 'italic' }}>—</span>}
                    {rowStatus[page.slug] && (
                      <span
                        style={
                          rowStatus[page.slug].type === 'success'
                            ? styles.successIndicator
                            : styles.errorIndicator
                        }
                      >
                        {rowStatus[page.slug].message}
                      </span>
                    )}
                  </span>
                )}
              </td>
              <td style={styles.td}>
                {editingSlug !== page.slug && (
                  <button
                    style={iconBtn}
                    onClick={() => startEdit(page)}
                    title="Edit alias"
                    aria-label={`Edit alias for ${page.slug}`}
                  >
                    ✏️
                  </button>
                )}
              </td>
            </tr>
          ))}
          {filteredPages.length === 0 && (
            <tr>
              <td style={{ ...styles.td, textAlign: 'center', color: TEXT_MUTED }} colSpan={5}>
                No screens match your search.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
